import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { SuccessModalComponent } from '@shared/components/parking-lot/success-modal/success-modal.component';
import { CustomValidator } from '@shared/services/custom-validatior.abstract';

@Component({
  selector: 'app-refund-payment-modal',
  templateUrl: './refund-payment-modal.component.html',
  styleUrls: ['./refund-payment-modal.component.scss']
})
export class RefundPaymentModalComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  refundForm: FormGroup;
  totalPaidAmmount: number;
  bookingId: number;
  errorText: string;
  showError: boolean = false;

  cancellationReason = [
    { reason: 'Duplicate', value: '1' },
    { reason: 'Request by the customer', value: '2' },
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly superAdminService: SuperAdminService,
    private readonly dialogControlService: DialogControlService
  ) { }

  ngOnInit() {
    this.initializeForm();
  }

  initializeForm() {
    this.totalPaidAmmount = this.config.data?.totalPaidAmmount;
    this.bookingId = this.config.data?.bookingId || null;

    this.refundForm = this.fb.group({
      refundAmount: [`$${this.totalPaidAmmount}`, [
        Validators.required,
        this.refundAmountValidator(this.totalPaidAmmount)
      ]],
      cancellationReason: ['', Validators.required],
      additionalComments: [''],
    });

}

  refundAmountValidator(maxAmount: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const numericValue = Number(String(control.value).replace(/[^0-9.]/g, ''));

      if (isNaN(numericValue)) return { invalidNumber: true };
      if (numericValue < 1) return { minAmount: true };
      if (numericValue > maxAmount) return { maxAmount: true };

      return null;
    };
  }

  onlyNumber(event: any) {
  const charCode = event.which ? event.which : event.keyCode;
  const input = event.target as HTMLInputElement;
  const value = input.value;

  if (charCode >= 48 && charCode <= 57) {
    if (value.includes('.')) {
      const decimalIndex = value.indexOf('.');
      const decimalPart = value.substring(decimalIndex + 1);

      if (input.selectionStart! > decimalIndex && decimalPart.length >= 2) {
        event.preventDefault();
      }
    }
    return;
  }
  if (charCode === 46 && !value.includes('.')) {
    return;
  }
  event.preventDefault();
}

validateDecimal(event: any) {
  const input = event.target as HTMLInputElement;
  if (input.value.endsWith('.')) {
    input.value = input.value.slice(0, -1); // remove trailing dot
  }
}

  formatValue(event: any) {
    let input = event.target.value;
    input = input.replace(/[^0-9.]/g, '');

    if (input) {
      event.target.value = `$${input}`;
    } else {
      event.target.value = '$';
    }
    this.refundForm.get('refundAmount')?.setValue(event.target.value, {
      emitEvent: false
    });
  }

  onSubmit(): void {
    if (this.refundForm.valid) {
      const request = {
        bookingId: this.bookingId,
        refundAmount: `${this.getRefundAmount()}`,
        refundReason: this.refundForm.value.cancellationReason,
        refundReasonAdditionalNote: this.refundForm.value.additionalComments
      };

      this.superAdminService.customBookingRefundBySuperAdmin(request)
        .pipe(autoUnsubWith(this.destroyRef))
        .subscribe({
          next: (_response) => {
            this.closeDialog();
            this.dialogControlService.openDialog(SuccessModalComponent, {
              width: '20%',
              data: {
                bodyText: "Refund has been successfully initiated.",
                skipNavigation: true,
                refundAmount: this.getRefundAmount()
              }
            });

          },
          error: (error) => {
            this.showError = true;
            this.errorText = error.error.message;
          },
        });
    }
    else {
      CustomValidator.markFormGroupTouched(this.refundForm);
      this.showError = true;
      const validationResult = this.checkRefundFormValidation(this.refundForm);
      this.errorText = validationResult.message;
    }
  }

  checkRefundFormValidation(formGroup: FormGroup): { message: string } {
    const refundAmount = formGroup.get('refundAmount');
    const cancellationReason = formGroup.get('cancellationReason');

    const rules = [
      { field: refundAmount, error: 'required', message: 'Refund amount is required' },
      { field: refundAmount, error: 'invalidNumber', message: 'Refund amount must be a valid number' },
      { field: refundAmount, error: 'minAmount', message: 'The refund amount must be at least $1' },
      { field: refundAmount, error: 'maxAmount', message: 'Refund amount must be less than or equal to total paid amount' },
      { field: cancellationReason, error: 'required', message: 'Please select a reason' },
    ];

    const failedRule = rules.find(rule => rule.field?.hasError(rule.error));
    return { message: failedRule?.message || '' };
  }

  getRefundAmount(): number {
    const value = this.refundForm.get('refundAmount')?.value || '';
    return Number(String(value).replace(/[^0-9.]/g, '')) || 0;
  }

  closeDialog() {
    this.ref.close();
  }
}
